import * as THREE from 'three'
import {
  CHUNK_SCHEMA_VERSION,
  ROAD_SAMPLE_FLAGS,
  type CompiledWorldChunk,
} from '../../chunk-schema/src/index.ts'
import {
  createLocalMetricFrame,
  createMacrotileMetricFrame,
  macrotileAtGeoPoint,
  projectGeoPointToFrame,
  tileKey,
  unprojectFramePoint,
  WORLD_REGION_SCHEMA_VERSION,
  type GeoPoint,
  type LocalMetricFrame,
  type MacrotileId,
} from '../../world-model/src/index.ts'
import type { CachedMacroTile } from './chunk-cache.ts'
import { ChunkInstance, type SurfaceCoverage } from './chunk-instance.ts'
import {
  chunkContainsWorldPoint,
  chunkDistanceToWorldPoint,
  nearestChunkRoad,
  sampleChunkTerrain,
  sampleChunkTerrainValidity,
  type NearestRoadSample,
} from './chunk-sampling.ts'
import { createRuntimeMaterials } from './materials.ts'
import type { WorldPipelineInput, WorldPipelineRequest, WorldPipelineResponse } from './pipeline-contract.ts'
import {
  acceptPipelineBatch,
  completePipelineBatches,
  type PipelineBatchAccumulator,
} from './pipeline-assembly.ts'
import { RUNTIME_MESH_KINDS, type RuntimeMeshKind } from './runtime-contract.ts'
import type { RoadDebugPage, RoadDebugStats } from './road-debug-overlay.ts'
import {
  planStreamingTraversal,
  publishedEnvelopeContains,
  type PublishedBounds2,
} from './streaming-boundary.ts'
import {
  pendingPrefetchAction,
  rankedPrefetchPoints,
  retainDiscontinuousPrefetchPoint,
  retainMotionPrefetchPoint,
  retainPendingPrefetch,
  retainPrefetchDirection,
  selectSinglePrefetchCandidate,
  type PrefetchDirection,
  type PrefetchPoint,
} from './streaming-prefetch.ts'
import { resolveMotionAgainstBlockingSegments } from './structure-collision.ts'

const RESIDENT_RADIUS_METERS = 680
const EVICTION_RADIUS_METERS = 920
const MACRO_EVICTION_RADIUS_METERS = 3_400
const PREFETCH_LOOKAHEAD_METERS = 520
const PREFETCH_DISCONTINUITY_METERS = 32
const CHUNK_INSTANTIATION_INTERVAL_MS = 32
const MOUNT_BUDGET_MS = 4
const SPAWN_NEIGHBOURHOOD_CHUNKS = 9
const RETIRE_BUDGET_MS = 2
const MOUNT_OPERATIONS_PER_FRAME = 2
const ROAD_METADATA_LATERAL_MARGIN_METERS = 0.85
const MAX_RENDER_SURFACE_VERTICAL_DELTA_METERS = 3.2

type SurfaceContact = {
  height: number
  grip: number
  road: boolean
  name?: string
}

type SpawnPoint = {
  x: number
  z: number
  heading: number
  chunkKey?: string
  sampleHeight?: number
}

type SpawnDiagnostic = {
  readonly chunkKey: string
  readonly surfaceCovered: boolean
  readonly coveringMeshId?: string
  readonly coveringMeshKind?: string
  readonly coveringMeshMounted: boolean
  readonly coveringHeight?: number
  readonly roadSampleHeight?: number
  readonly terrainHeight?: number
  readonly terrainValid: boolean
  readonly groupAttached: boolean
  readonly macroOffsetErrorMeters: number
}

export type BoundaryRoadProbe = {
  readonly roadName: string
  readonly before: { readonly x: number; readonly z: number; readonly height: number; readonly heading: number }
  readonly after: { readonly x: number; readonly z: number; readonly height: number; readonly heading: number }
}

export type SurfaceAuthorityProbe = {
  readonly chunkKey: string
  readonly macrotileKey: string
  readonly distanceToChunkMeters: number
  readonly terrainHeight?: number
  readonly terrainValid: boolean
  readonly road?: NearestRoadSample
  readonly rendered?: {
    readonly meshId: string
    readonly kind: string
    readonly triangleIndex: number
    readonly height: number
    readonly mounted: boolean
  }
}

type RuntimeMacro = CachedMacroTile & {
  readonly key: string
  readonly frame: LocalMetricFrame
  readonly offset: { readonly x: number; readonly z: number }
}

type WorkerPending = PipelineBatchAccumulator<CompiledWorldChunk> & {
  readonly resolve: (result: CachedMacroTile) => void
  readonly reject: (error: Error) => void
  readonly primary: boolean
}

type PerformanceEvent = {
  readonly phase: string
  readonly startTime: number
  readonly duration: number
}

type PendingPrefetch = {
  readonly macrotile: MacrotileId
  readonly key: string
  readonly generation: number
}

type MotionPrefetchIntent = PrefetchPoint & {
  readonly generation: number
  readonly direction?: PrefetchDirection
}

type StreamingPrefetchTransition = {
  readonly sequence: number
  readonly event: 'queued' | 'wait' | 'start' | 'complete' | 'discard'
  readonly key: string
  readonly generation: number
}

export type WorldStreamerState = {
  residentChunks: number
  availableChunks: number
  progress: number
  status: string
  currentMacro: string
  generation: number
  sourceWays: number
  solvedRoads: number
  queuedMacros: number
  workerBusy: boolean
  lastCompileMilliseconds: number
  /** Compiled chunk payload schema used between the worker and runtime. */
  schemaVersion: number
  /** Geographic source schema accepted by the OSM interpreter. */
  worldRegionSchemaVersion: number
  readyMacroKeys: string[]
  currentMacroReady: boolean
  /** Rendered terrain, roads and gameplay-critical vertical structures. */
  mountedMeshKinds: RuntimeMeshKind[]
  blockingStructureSegments: number
  debugPage: RoadDebugPage
  debugStats: RoadDebugStats
  spawnDiagnostic?: SpawnDiagnostic
  performance: {
    pipelineDeliveryMilliseconds: number
    registerMacroMilliseconds: number
    mountStepMilliseconds: number
    criticalMountStepMilliseconds: number
    retirementStepMilliseconds: number
    workerMessageHandlerMilliseconds: number
    clearWorldMilliseconds: number
    events: PerformanceEvent[]
  }
  streamingPrefetch: {
    probeOrigin?: PrefetchPoint
    observedPosition?: PrefetchPoint
    retainedMotionIntent?: PrefetchPoint
    candidateKey?: string
    pendingKey?: string
    inFlightKeys: string[]
    transitions: StreamingPrefetchTransition[]
  }
}

export class WorldStreamer {
  private readonly scene: THREE.Scene
  private readonly materials = createRuntimeMaterials()
  private readonly macros = new Map<string, RuntimeMacro>()
  private readonly loadingMacros = new Map<string, Promise<RuntimeMacro>>()
  private readonly instances = new Map<string, ChunkInstance>()
  /** Complete chunks are built here and become visible in one scene mutation. */
  private readonly stagingInstances = new Map<string, ChunkInstance>()
  private readonly retiredInstances: ChunkInstance[] = []
  private readonly retiredMacroChunks: CompiledWorldChunk[][] = []
  private readonly workerPending = new Map<string, WorkerPending>()
  private worldRoot = new THREE.Group()
  private worker!: Worker
  private workerReady!: Promise<void>
  private workerReadyResolve?: () => void
  private sessionFrame = createLocalMetricFrame({ lat: -33.8795, lon: 151.1035 })
  private generation = 0
  private requestSequence = 0
  private currentMacroKey = ''
  private status = 'WAITING FOR COORDINATES'
  private progress = 0
  private lastCompileMilliseconds = 0
  private lastInstantiationAt = 0
  private lastPrefetchAt = 0
  private pendingPrefetch?: PendingPrefetch
  private motionPrefetchIntent?: MotionPrefetchIntent
  private lastUpdatePosition?: PrefetchPoint
  private lastPrefetchProbe?: PrefetchPoint
  private lastPrefetchCandidateKey?: string
  private readonly prefetchLoads = new Set<string>()
  private prefetchTransitionSequence = 0
  private prefetchTransitions: StreamingPrefetchTransition[] = []
  private mountCursor = 0
  private lastSurfaceHeight = 0
  private spawn: SpawnPoint = { x: 0, z: 0, heading: 0 }
  private spawnDiagnostic?: SpawnDiagnostic
  private debugPage: RoadDebugPage = 0
  private criticalMountInProgress = false
  private performanceDiagnostics = {
    pipelineDeliveryMilliseconds: 0,
    registerMacroMilliseconds: 0,
    mountStepMilliseconds: 0,
    criticalMountStepMilliseconds: 0,
    retirementStepMilliseconds: 0,
    workerMessageHandlerMilliseconds: 0,
    clearWorldMilliseconds: 0,
  }
  private performanceEvents: PerformanceEvent[] = []

  constructor(options: { scene: THREE.Scene }) {
    this.scene = options.scene
    this.worldRoot.name = 'STREAMED_WORLD'
    this.scene.add(this.worldRoot)
    this.createWorker()
  }

  async prepareRenderer(renderer: THREE.WebGLRenderer, camera: THREE.Camera): Promise<void> {
    const warmup = new THREE.Group()
    warmup.name = 'PROCEDURAL_MATERIAL_WARMUP'
    warmup.position.set(0, -10_000, 0)
    const geometry = new THREE.BufferGeometry()
    geometry.setAttribute('position', new THREE.Float32BufferAttribute([-1, 0, -1, 1, 0, -1, 0, 0, 1], 3))
    geometry.setAttribute('normal', new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0], 3))
    geometry.setIndex([0, 1, 2])
    for (const material of this.materials.values()) {
      const mesh = new THREE.Mesh(geometry, material)
      mesh.frustumCulled = false
      mesh.castShadow = true
      mesh.receiveShadow = true
      warmup.add(mesh)
    }
    this.scene.add(warmup)
    try {
      await Promise.all([renderer.compileAsync(this.scene, camera), this.workerReady])
    } finally {
      this.scene.remove(warmup)
      geometry.dispose()
    }
  }

  async teleport(latitude: number, longitude: number): Promise<void> {
    const generation = ++this.generation
    if (this.workerPending.size > 0) this.resetWorker()
    await this.workerReady
    this.performanceEvents = []
    const clearStartedAt = performance.now()
    this.clearWorld()
    this.performanceDiagnostics.clearWorldMilliseconds = performance.now() - clearStartedAt
    this.recordPerformanceEvent('clear-world', clearStartedAt)
    this.sessionFrame = createLocalMetricFrame({ lat: latitude, lon: longitude })
    const macrotile = macrotileAtGeoPoint({ lat: latitude, lon: longitude })
    this.currentMacroKey = tileKey(macrotile)
    this.status = 'CHECKING LOCAL WORLD CACHE'
    this.progress = 0.04
    this.performanceDiagnostics = {
      pipelineDeliveryMilliseconds: 0,
      registerMacroMilliseconds: 0,
      mountStepMilliseconds: 0,
      criticalMountStepMilliseconds: 0,
      retirementStepMilliseconds: 0,
      workerMessageHandlerMilliseconds: 0,
      clearWorldMilliseconds: this.performanceDiagnostics.clearWorldMilliseconds,
    }
    try {
      const runtime = await this.ensureMacro(macrotile, generation, true)
      if (generation !== this.generation) return
      this.spawn = this.findBestSpawn(runtime)
      this.instantiateNearest(this.spawn.x, this.spawn.z, SPAWN_NEIGHBOURHOOD_CHUNKS, false)
      if (this.spawn.chunkKey && !this.instances.has(this.spawn.chunkKey) && !this.stagingInstances.has(this.spawn.chunkKey)) {
        const spawnChunk = runtime.chunks.find((chunk) => tileKey(chunk.id) === this.spawn.chunkKey)
        if (spawnChunk) this.mountChunk(runtime, spawnChunk, false)
      }
      this.progress = 0.97
      this.status = 'MOUNTING SPAWN NEIGHBOURHOOD'
      await this.mountSpawnNeighbourhood(this.spawn.x, this.spawn.z, generation)
      if (generation !== this.generation) return
      if (this.spawn.chunkKey) {
        const spawnInstance = this.instances.get(this.spawn.chunkKey)
        spawnInstance?.mountCriticalSurface()
        if (spawnInstance) {
          const coverage = spawnInstance.surfaceCoverageAtWorld(this.spawn.x, this.spawn.z, this.spawn.sampleHeight)
          this.spawnDiagnostic = {
            chunkKey: this.spawn.chunkKey,
            surfaceCovered: coverage !== undefined,
            coveringMeshId: coverage?.meshId,
            coveringMeshKind: coverage?.kind,
            coveringMeshMounted: coverage?.mounted ?? false,
            coveringHeight: coverage?.height,
            roadSampleHeight: this.spawn.sampleHeight,
            terrainHeight: spawnInstance.sampleTerrainWorld(this.spawn.x, this.spawn.z),
            terrainValid: spawnInstance.sampleTerrainValidityWorld(this.spawn.x, this.spawn.z),
            groupAttached: spawnInstance.group.parent === this.worldRoot,
            macroOffsetErrorMeters: Math.hypot(spawnInstance.macroOffset.x - runtime.offset.x, spawnInstance.macroOffset.y - runtime.offset.z),
          }
        }
      }
      const spawnSurface = this.sampleSurface(this.spawn.x, this.spawn.z, this.spawn.sampleHeight)
      this.lastSurfaceHeight = spawnSurface.height
      this.progress = 1
      this.status = 'WORLD READY · STREAMING AHEAD'
    } catch (cause) {
      if (generation !== this.generation) return
      const error = cause instanceof Error ? cause : new Error(String(cause))
      this.progress = 0
      this.status = `WORLD SOURCE FAILED · ${error.message}`
      throw error
    }
  }

  update(x: number, z: number, directionX = 0, directionZ = 0): void {
    const priorIntent = this.motionPrefetchIntent?.generation === this.generation
      ? this.motionPrefetchIntent
      : undefined
    const discontinuousIntent = retainDiscontinuousPrefetchPoint(
      priorIntent,
      this.lastUpdatePosition,
      { x, z },
      PREFETCH_DISCONTINUITY_METERS,
    )
    const observedDiscontinuity = priorIntent === undefined && discontinuousIntent !== undefined
    const retainedDirection = discontinuousIntent === undefined
      ? undefined
      : retainPrefetchDirection(
          priorIntent?.direction,
          discontinuousIntent,
          { x, z },
          { x: directionX, z: directionZ },
        )
    this.motionPrefetchIntent = discontinuousIntent === undefined
      ? undefined
      : {
          x: discontinuousIntent.x,
          z: discontinuousIntent.z,
          generation: this.generation,
          direction: retainedDirection,
        }
    const geo = this.localToGeo(x, z)
    const current = macrotileAtGeoPoint(geo)
    const nextKey = tileKey(current)
    if (nextKey !== this.currentMacroKey) {
      this.currentMacroKey = nextKey
      void this.ensureMacro(current, this.generation, true).catch(() => undefined)
    }

    const now = performance.now()
    if (observedDiscontinuity || now - this.lastPrefetchAt > 320) {
      this.lastPrefetchAt = now
      const intendedDestination = this.motionPrefetchIntent?.generation === this.generation
        ? this.motionPrefetchIntent
        : undefined
      this.motionPrefetchIntent = undefined
      // A collision/recovery may move the rendered car back inside the safe
      // envelope before this tick. Keep queue selection anchored to the pose
      // the simulation attempted, while presentation and collision remain at
      // the published position.
      const probeX = intendedDestination?.x ?? x
      const probeZ = intendedDestination?.z ?? z
      const probeDirectionX = intendedDestination?.direction?.x ?? directionX
      const probeDirectionZ = intendedDestination?.direction?.z ?? directionZ
      this.prefetchAround(probeX, probeZ, probeDirectionX, probeDirectionZ, intendedDestination)
      this.evictDistantMacros(x, z)
    }
    if (now - this.lastInstantiationAt >= CHUNK_INSTANTIATION_INTERVAL_MS) {
      const created = this.instantiateNearest(x, z, 1, false)
      if (created > 0) this.lastInstantiationAt = now
    }
    this.evictDistantChunks(x, z)
    this.lastUpdatePosition = { x, z }
  }

  pump(): void {
    if (!this.criticalMountInProgress) this.processMountBudget(MOUNT_BUDGET_MS)
    this.processRetirementBudget(RETIRE_BUDGET_MS)
  }

  setDebugPage(page: RoadDebugPage): void {
    if (page === this.debugPage) return
    this.debugPage = page
    for (const instance of this.instances.values()) instance.setDebugPage(page)
  }

  cycleDebugPage(): RoadDebugPage {
    const page = ((this.debugPage + 1) % 3) as RoadDebugPage
    this.setDebugPage(page)
    return page
  }

  getDebugStats(): RoadDebugStats {
    const total: RoadDebugStats = {
      splineSegments: 0,
      nodes: 0,
      roadTriangles: 0,
      junctionTriangles: 0,
    }
    for (const instance of this.instances.values()) {
      const stats = instance.debugStats
      total.splineSegments += stats.splineSegments
      total.nodes += stats.nodes
      total.roadTriangles += stats.roadTriangles
      total.junctionTriangles += stats.junctionTriangles
    }
    return total
  }

  sampleSurface(x: number, z: number, referenceY?: number): SurfaceContact {
    let terrain: number | undefined
    const nearbyInstances: ChunkInstance[] = []
    const preliminaryRoadCandidates: NearestRoadSample[] = []
    const callerReference = Number.isFinite(referenceY) ? referenceY as number : undefined
    // Simulation authority is published with presentation authority. A fully
    // compiled but still-staging chunk must not become an invisible collider.
    for (const instance of this.instances.values()) {
      const chunk = instance.data
      const offset = { x: instance.macroOffset.x, z: instance.macroOffset.y }
      if (terrain === undefined && chunkContainsWorldPoint(chunk, offset, x, z, 0.02)) {
        terrain = sampleChunkTerrain(chunk, offset, x, z)
      }
      if (chunkDistanceToWorldPoint(chunk, offset, x, z) > 18) continue
      nearbyInstances.push(instance)
      if (callerReference !== undefined) continue
      const road = nearestChunkRoad(chunk, offset, x, z)
      if (road && road.distance <= road.width * 0.5 + ROAD_METADATA_LATERAL_MARGIN_METERS) {
        preliminaryRoadCandidates.push(road)
      }
    }

    const groundHeight = terrain ?? this.lastSurfaceHeight
    let preliminaryRoad: NearestRoadSample | undefined
    for (const candidate of preliminaryRoadCandidates) {
      if (!preliminaryRoad || candidate.distance < preliminaryRoad.distance) preliminaryRoad = candidate
    }
    // Every current caller supplies a wheel/chassis ground reference, not a
    // chassis-centre height. The old -0.34 m correction put simulation below
    // the exact rendered plane at sloped junctions.
    const referenceSurface = callerReference !== undefined
      ? callerReference
      : preliminaryRoad?.height ?? groundHeight
    let rendered: { readonly coverage: SurfaceCoverage; readonly instance: ChunkInstance } | undefined
    let renderedHeightDelta = Number.POSITIVE_INFINITY
    for (const instance of nearbyInstances) {
      const candidate = instance.surfaceCoverageAtWorld(x, z, referenceSurface)
      if (!candidate) continue
      const heightDelta = Math.abs(candidate.height - referenceSurface)
      if (rendered && heightDelta >= renderedHeightDelta) continue
      rendered = { coverage: candidate, instance }
      renderedHeightDelta = heightDelta
    }

    if (rendered && renderedHeightDelta <= MAX_RENDER_SURFACE_VERTICAL_DELTA_METERS) {
      // Metadata comes from the chunk that owns the selected render triangle.
      // A neighbouring macrotile's solve halo can contain a closer but
      // non-owning centreline from the far side of the junction.
      const metadataInstance = rendered.instance
      const selectedMetadata = nearestChunkRoad(
        metadataInstance.data,
        { x: metadataInstance.macroOffset.x, z: metadataInstance.macroOffset.y },
        x,
        z,
        {
          referenceHeight: rendered.coverage.height,
          maximumLateralMargin: ROAD_METADATA_LATERAL_MARGIN_METERS,
          maximumVerticalDelta: MAX_RENDER_SURFACE_VERTICAL_DELTA_METERS,
        },
      )
      this.lastSurfaceHeight = rendered.coverage.height
      const path = selectedMetadata !== undefined && (selectedMetadata.flags & ROAD_SAMPLE_FLAGS.path) !== 0
      return {
        height: rendered.coverage.height,
        grip: path ? 0.68 : 1,
        road: !path,
        ...(selectedMetadata === undefined ? {} : { name: selectedMetadata.roadId }),
      }
    }

    // Packed centrelines remain road-name, grip and layer metadata only. They
    // must never provide invisible vertical support when no published triangle
    // covers the queried point.
    this.lastSurfaceHeight = groundHeight
    return { height: groundHeight, grip: 0.46, road: false }
  }

  /**
   * Inspect the independently published render, centreline-contact and terrain
   * authorities around one world point. This is intentionally read-only and is
   * used by coordinate regressions to catch a visually paved surface whose
   * gameplay support comes from a different height or neighbouring macrotile.
   */
  probeSurfaceAuthorities(x: number, z: number, referenceY?: number): readonly SurfaceAuthorityProbe[] {
    const probes: SurfaceAuthorityProbe[] = []
    for (const [chunkKey, instance] of this.instances) {
      const distanceToChunkMeters = instance.distanceToWorldPoint(x, z)
      if (distanceToChunkMeters > 18) continue
      const offset = { x: instance.macroOffset.x, z: instance.macroOffset.y }
      const rendered = instance.surfaceCoverageAtWorld(x, z, referenceY)
      const road = nearestChunkRoad(instance.data, offset, x, z, rendered === undefined
        ? {}
        : {
            referenceHeight: rendered.height,
            maximumLateralMargin: ROAD_METADATA_LATERAL_MARGIN_METERS,
            maximumVerticalDelta: MAX_RENDER_SURFACE_VERTICAL_DELTA_METERS,
          })
      probes.push({
        chunkKey,
        macrotileKey: tileKey(instance.data.macrotile),
        distanceToChunkMeters,
        terrainHeight: instance.sampleTerrainWorld(x, z),
        terrainValid: instance.sampleTerrainValidityWorld(x, z),
        ...(road === undefined ? {} : { road }),
        ...(rendered === undefined ? {} : { rendered }),
      })
    }
    return probes.sort((left, right) =>
      left.distanceToChunkMeters - right.distanceToChunkMeters
      || left.macrotileKey.localeCompare(right.macrotileKey, 'en')
      || left.chunkKey.localeCompare(right.chunkKey, 'en')
    )
  }

  isTerrainSourceValid(x: number, z: number): boolean {
    for (const instance of this.instances.values()) {
      const offset = { x: instance.macroOffset.x, z: instance.macroOffset.y }
      if (!chunkContainsWorldPoint(instance.data, offset, x, z, 0.02)) continue
      return sampleChunkTerrainValidity(instance.data, offset, x, z)
    }
    return false
  }

  /**
   * Resolve authored structure collisions while treating missing streamed
   * coverage as an urgent load request, never as invisible collision geometry.
   */
  resolveStreamingMotion(
    startX: number,
    startZ: number,
    endX: number,
    endZ: number,
    minY: number,
    maxY: number,
    radiusMeters: number,
  ): { x: number; z: number; collided: boolean } {
    const bounds: PublishedBounds2[] = [...this.instances.values()].map((instance) => ({
      minX: instance.data.localBounds.minX + instance.macroOffset.x,
      minZ: instance.data.localBounds.minZ + instance.macroOffset.y,
      maxX: instance.data.localBounds.maxX + instance.macroOffset.x,
      maxZ: instance.data.localBounds.maxZ + instance.macroOffset.y,
    }))
    const radius = Math.max(0, radiusMeters)
    const streamingTraversal = planStreamingTraversal(
      startX,
      startZ,
      endX,
      endZ,
      (x, z) => publishedEnvelopeContains(bounds, x, z, radius),
      Math.max(0.25, radius * 0.5),
    )
    const existingIntent = this.motionPrefetchIntent?.generation === this.generation
      ? this.motionPrefetchIntent
      : undefined
    const retainedIntent = retainMotionPrefetchPoint(
      existingIntent,
      this.lastUpdatePosition,
      { x: startX, z: startZ },
      { x: endX, z: endZ },
      streamingTraversal.coveragePending,
      PREFETCH_DISCONTINUITY_METERS,
    )
    const retainedExistingDirection = existingIntent !== undefined
      && retainedIntent?.x === existingIntent.x
      && retainedIntent.z === existingIntent.z
      ? existingIntent.direction
      : undefined
    this.motionPrefetchIntent = retainedIntent === undefined
      ? undefined
      : {
          x: retainedIntent.x,
          z: retainedIntent.z,
          generation: this.generation,
          direction: retainedExistingDirection,
        }
    const sweepMinX = Math.min(startX, streamingTraversal.x) - radius
    const sweepMinZ = Math.min(startZ, streamingTraversal.z) - radius
    const sweepMaxX = Math.max(startX, streamingTraversal.x) + radius
    const sweepMaxZ = Math.max(startZ, streamingTraversal.z) + radius
    const blockingSegments = [...this.instances.values()]
      .filter((instance) => {
        const chunkBounds = instance.data.localBounds
        const minX = chunkBounds.minX + instance.macroOffset.x
        const minZ = chunkBounds.minZ + instance.macroOffset.y
        const maxX = chunkBounds.maxX + instance.macroOffset.x
        const maxZ = chunkBounds.maxZ + instance.macroOffset.y
        return maxX >= sweepMinX && minX <= sweepMaxX && maxZ >= sweepMinZ && minZ <= sweepMaxZ
      })
      .flatMap((instance) => instance.blockingStructureSegments)
    const structureMotion = resolveMotionAgainstBlockingSegments(
      startX,
      startZ,
      streamingTraversal.x,
      streamingTraversal.z,
      minY,
      maxY,
      radius,
      blockingSegments,
    )
    return {
      x: structureMotion.x,
      z: structureMotion.z,
      collided: structureMotion.collided,
    }
  }

  findSpawn(): SpawnPoint {
    return { ...this.spawn }
  }

  /** Finds a real centreline crossing shared by two loaded macrotiles. */
  findBoundaryRoadProbe(fromMacroKey: string, toMacroKey: string): BoundaryRoadProbe | undefined {
    const from = this.macros.get(fromMacroKey)
    const to = this.macros.get(toMacroKey)
    if (!from || !to) return undefined
    const quantise = (value: number) => Math.round(value * 2)
    const keyFor = (roadName: string, x: number, z: number) => `${roadName}|${quantise(x)}|${quantise(z)}`
    const toBoundarySamples = new Map<string, Array<{ x: number; z: number }>>()
    for (const chunk of to.chunks) {
      const roads = chunk.roads
      for (let sample = 0; sample < roads.widths.length; sample += 1) {
        if ((roads.flags[sample] & ROAD_SAMPLE_FLAGS.path) !== 0) continue
        const roadName = roads.roadIds[roads.roadIndex[sample]] ?? 'UNNAMED ROAD'
        const x = roads.positions[sample * 3] + to.offset.x
        const z = roads.positions[sample * 3 + 2] + to.offset.z
        const key = keyFor(roadName, x, z)
        const entries = toBoundarySamples.get(key)
        if (entries) entries.push({ x, z })
        else toBoundarySamples.set(key, [{ x, z }])
      }
    }
    for (const chunk of from.chunks) {
      const roads = chunk.roads
      for (let sample = 0; sample < roads.widths.length; sample += 1) {
        if ((roads.flags[sample] & ROAD_SAMPLE_FLAGS.path) !== 0) continue
        const roadName = roads.roadIds[roads.roadIndex[sample]] ?? 'UNNAMED ROAD'
        const x = roads.positions[sample * 3] + from.offset.x
        const z = roads.positions[sample * 3 + 2] + from.offset.z
        const matches = toBoundarySamples.get(keyFor(roadName, x, z))
        if (!matches?.some((point) => Math.hypot(point.x - x, point.z - z) <= 0.75)) continue
        const tangentX = roads.tangents[sample * 2]
        const tangentZ = roads.tangents[sample * 2 + 1]
        const tangentLength = Math.hypot(tangentX, tangentZ)
        if (tangentLength < 0.5) continue
        for (const direction of [1, -1]) {
          const dx = tangentX / tangentLength * direction
          const dz = tangentZ / tangentLength * direction
          const before = { x: x - dx * 2, z: z - dz * 2 }
          const after = { x: x + dx * 2, z: z + dz * 2 }
          const beforeKey = tileKey(macrotileAtGeoPoint(this.localToGeo(before.x, before.z)))
          const afterKey = tileKey(macrotileAtGeoPoint(this.localToGeo(after.x, after.z)))
          if (beforeKey !== fromMacroKey || afterKey !== toMacroKey) continue
          const height = roads.positions[sample * 3 + 1]
          return {
            roadName,
            before: { ...before, height, heading: Math.atan2(dx, dz) },
            after: { ...after, height, heading: Math.atan2(dx, dz) },
          }
        }
      }
    }
    return undefined
  }

  localToGeo(x: number, z: number): GeoPoint {
    return unprojectFramePoint({ x, z }, this.sessionFrame)
  }

  getState(): WorldStreamerState {
    let sourceWays = 0
    let solvedRoads = 0
    let availableChunks = 0
    for (const runtime of this.macros.values()) {
      sourceWays += runtime.sourceWays
      solvedRoads += runtime.solvedRoads
      availableChunks += runtime.chunks.length
    }
    const readyMacroKeys = [...this.macros.keys()].sort()
    const currentMacroReady = this.macros.has(this.currentMacroKey)
      && [...this.instances.values()].some((instance) => tileKey(instance.data.macrotile) === this.currentMacroKey)
    const mountedKinds = new Set<RuntimeMeshKind>()
    let blockingStructureSegments = 0
    for (const instance of this.instances.values()) {
      for (const kind of instance.mountedMeshKinds) mountedKinds.add(kind)
      blockingStructureSegments += instance.blockingStructureSegments.length
    }
    return {
      residentChunks: this.instances.size,
      availableChunks,
      progress: this.progress,
      status: this.status,
      currentMacro: this.currentMacroKey,
      generation: this.generation,
      sourceWays,
      solvedRoads,
      queuedMacros: this.loadingMacros.size,
      workerBusy: this.workerPending.size > 0,
      lastCompileMilliseconds: this.lastCompileMilliseconds,
      schemaVersion: CHUNK_SCHEMA_VERSION,
      worldRegionSchemaVersion: WORLD_REGION_SCHEMA_VERSION,
      readyMacroKeys,
      currentMacroReady,
      mountedMeshKinds: RUNTIME_MESH_KINDS.filter((kind) => mountedKinds.has(kind)),
      blockingStructureSegments,
      debugPage: this.debugPage,
      debugStats: this.getDebugStats(),
      spawnDiagnostic: this.spawnDiagnostic,
      performance: { ...this.performanceDiagnostics, events: [...this.performanceEvents] },
      streamingPrefetch: {
        probeOrigin: this.lastPrefetchProbe,
        observedPosition: this.lastUpdatePosition,
        retainedMotionIntent: this.motionPrefetchIntent,
        candidateKey: this.lastPrefetchCandidateKey,
        pendingKey: this.pendingPrefetch?.key,
        inFlightKeys: [...this.loadingMacros.keys()].sort(),
        transitions: [...this.prefetchTransitions],
      },
    }
  }

  private async ensureMacro(macrotile: MacrotileId, generation: number, primary: boolean): Promise<RuntimeMacro> {
    const key = tileKey(macrotile)
    const resident = this.macros.get(key)
    if (resident) return resident
    const existing = this.loadingMacros.get(key)
    if (existing) return existing
    const task = this.loadMacro(macrotile, generation, primary)
    this.loadingMacros.set(key, task)
    try {
      return await task
    } finally {
      if (this.loadingMacros.get(key) === task) {
        this.loadingMacros.delete(key)
        if (this.prefetchLoads.delete(key)) this.recordPrefetchTransition('complete', key, generation)
        this.drainPendingPrefetch()
      }
    }
  }

  private async loadMacro(macrotile: MacrotileId, generation: number, primary: boolean): Promise<RuntimeMacro> {
    const key = tileKey(macrotile)
    if (primary) {
      this.progress = 0.04
      this.status = 'CHECKING LOCAL WORLD CACHE'
    } else if (this.progress >= 1) this.status = `STREAMING ${key}`
    const result = await this.loadInWorker({
      macrotile,
      terrainDivisions: 12,
      civil: {
        maximumGroundGrade: 0.14,
        maximumStructureGrade: 0.075,
        bridgeClearanceMeters: 5,
        tunnelCoverMeters: 2.5,
      },
    }, primary)
    this.assertGeneration(generation)
    this.lastCompileMilliseconds = result.compileMilliseconds
    const runtime = this.registerMacro(result)
    if (primary) {
      this.progress = 0.94
      this.status = 'MOUNTING NEARBY CHUNKS'
    } else if (this.progress >= 1) this.status = 'WORLD READY · STREAMING AHEAD'
    return runtime
  }

  private registerMacro(value: CachedMacroTile): RuntimeMacro {
    const startedAt = performance.now()
    const frame = value.chunks[0]?.frame ?? createMacrotileMetricFrame(value.macrotile)
    const offsetPoint = projectGeoPointToFrame(frame.anchor, this.sessionFrame)
    const runtime: RuntimeMacro = {
      ...value,
      key: tileKey(value.macrotile),
      frame,
      offset: { x: offsetPoint.x, z: offsetPoint.z },
    }
    this.macros.set(runtime.key, runtime)
    this.performanceDiagnostics.registerMacroMilliseconds = Math.max(
      this.performanceDiagnostics.registerMacroMilliseconds,
      performance.now() - startedAt,
    )
    return runtime
  }

  private loadInWorker(input: WorldPipelineInput, primary: boolean): Promise<CachedMacroTile> {
    const requestId = `${this.generation}:${++this.requestSequence}`
    return new Promise((resolve, reject) => {
      this.workerPending.set(requestId, { resolve, reject, primary, batches: [] })
      const request: WorldPipelineRequest = { type: 'load-macrotile', requestId, input }
      this.worker.postMessage(request)
    })
  }

  private createWorker(): void {
    this.workerReady = new Promise((resolve) => { this.workerReadyResolve = resolve })
    this.worker = new Worker(new URL('./pipeline.worker.ts', import.meta.url), { type: 'module', name: 'daylight-world-pipeline' })
    this.worker.onmessage = (event: MessageEvent<WorldPipelineResponse>) => {
      const handlerStartedAt = performance.now()
      const response = event.data
      if (response.type === 'pipeline-ready') {
        this.workerReadyResolve?.()
        this.workerReadyResolve = undefined
        return
      }
      const pending = this.workerPending.get(response.requestId)
      if (!pending) return
      if (response.type === 'macrotile-progress') {
        if (pending.primary) this.applyPipelineProgress(response.stage, response.sourceWays)
        return
      }
      if (response.type === 'macrotile-chunk-batch') {
        const delivery = performance.timeOrigin + performance.now() - response.postedAtEpochMilliseconds
        this.performanceDiagnostics.pipelineDeliveryMilliseconds = Math.max(
          this.performanceDiagnostics.pipelineDeliveryMilliseconds,
          delivery,
        )
        try {
          acceptPipelineBatch(pending, response.batchIndex, response.batchCount, response.chunks)
        } catch (cause) {
          this.workerPending.delete(response.requestId)
          pending.reject(cause instanceof Error ? cause : new Error(String(cause)))
          return
        }
        const duration = performance.now() - handlerStartedAt
        this.performanceDiagnostics.workerMessageHandlerMilliseconds = Math.max(
          this.performanceDiagnostics.workerMessageHandlerMilliseconds,
          duration,
        )
        this.addPerformanceEvent('worker-batch-delivery', Math.max(0, handlerStartedAt - delivery), delivery)
        this.addPerformanceEvent('worker-batch-handler', handlerStartedAt, duration)
        return
      }
      this.workerPending.delete(response.requestId)
      if (response.type === 'macrotile-ready') {
        const delivery = performance.timeOrigin + performance.now() - response.postedAtEpochMilliseconds
        this.performanceDiagnostics.pipelineDeliveryMilliseconds = Math.max(
          this.performanceDiagnostics.pipelineDeliveryMilliseconds,
          delivery,
        )
        let chunks: CompiledWorldChunk[]
        try {
          chunks = completePipelineBatches(pending, response.batchCount, (chunk) => tileKey(chunk.id))
          const expectedMacroKey = tileKey(response.result.macrotile)
          const wrongOwner = chunks.find((chunk) => tileKey(chunk.macrotile) !== expectedMacroKey)
          if (wrongOwner) throw new Error(`World pipeline delivered chunk ${tileKey(wrongOwner.id)} for the wrong macrotile.`)
        } catch (cause) {
          pending.reject(cause instanceof Error ? cause : new Error(String(cause)))
          return
        }
        pending.resolve({ ...response.result, chunks })
      }
      else {
        const error = new Error(response.error.message)
        error.name = response.error.name
        if (response.error.stack) error.stack = response.error.stack
        pending.reject(error)
      }
      const duration = performance.now() - handlerStartedAt
      this.performanceDiagnostics.workerMessageHandlerMilliseconds = Math.max(
        this.performanceDiagnostics.workerMessageHandlerMilliseconds,
        duration,
      )
      this.addPerformanceEvent('worker-final-handler', handlerStartedAt, duration)
    }
    this.worker.onerror = (event) => {
      const error = new Error(event.message || 'World compiler worker failed.')
      for (const pending of this.workerPending.values()) pending.reject(error)
      this.workerPending.clear()
    }
  }

  private applyPipelineProgress(stage: 'cache' | 'source' | 'compile' | 'persist', sourceWays?: number): void {
    if (stage === 'cache') {
      this.progress = 0.05
      this.status = 'CHECKING LOCAL WORLD CACHE'
    } else if (stage === 'source') {
      this.progress = 0.12
      this.status = 'FETCHING ROADS + ELEVATION OFF THREAD'
    } else if (stage === 'compile') {
      this.progress = 0.5
      this.status = `SOLVING ${(sourceWays ?? 0).toLocaleString()} SOURCE WAYS OFF THREAD`
    } else {
      this.progress = 0.9
      this.status = 'PERSISTING COMPILED CHUNKS OFF THREAD'
    }
  }

  private resetWorker(): void {
    if (this.worker) this.worker.terminate()
    for (const pending of this.workerPending.values()) pending.reject(new Error('World compile superseded by teleport.'))
    this.workerPending.clear()
    this.createWorker()
  }

  private instantiateNearest(x: number, z: number, limit: number, immediate: boolean): number {
    const candidates: Array<{ runtime: RuntimeMacro; chunk: CompiledWorldChunk; distance: number }> = []
    for (const runtime of this.macros.values()) {
      for (const chunk of runtime.chunks) {
        const key = tileKey(chunk.id)
        if (this.instances.has(key) || this.stagingInstances.has(key)) continue
        const distance = chunkDistanceToWorldPoint(chunk, runtime.offset, x, z)
        if (distance <= RESIDENT_RADIUS_METERS || immediate) candidates.push({ runtime, chunk, distance })
      }
    }
    candidates.sort((left, right) => left.distance - right.distance || tileKey(left.chunk.id).localeCompare(tileKey(right.chunk.id)))
    let created = 0
    for (const candidate of candidates.slice(0, limit)) {
      this.mountChunk(candidate.runtime, candidate.chunk, immediate)
      created += 1
    }
    return created
  }

  private mountChunk(runtime: RuntimeMacro, chunk: CompiledWorldChunk, immediate = false): void {
    const key = tileKey(chunk.id)
    if (this.instances.has(key) || this.stagingInstances.has(key)) return
    const instance = new ChunkInstance(
      chunk,
      new THREE.Vector2(runtime.offset.x, runtime.offset.z),
      this.materials,
    )
    instance.setDebugPage(this.debugPage)
    if (immediate) {
      instance.mountAllCriticalSurfaces()
      this.publishChunk(key, instance)
    } else {
      this.stagingInstances.set(key, instance)
    }
  }

  private publishChunk(key: string, instance: ChunkInstance): void {
    if (this.instances.has(key)) return
    if (!instance.isReadyForPublication) throw new Error(`Refused to publish incomplete chunk ${key}.`)
    this.stagingInstances.delete(key)
    this.worldRoot.add(instance.group)
    this.instances.set(key, instance)
  }

  private removeChunk(key: string): void {
    const staging = this.stagingInstances.get(key)
    if (staging) {
      this.stagingInstances.delete(key)
      this.retiredInstances.push(staging)
    }
    const instance = this.instances.get(key)
    if (!instance) return
    this.worldRoot.remove(instance.group)
    this.retiredInstances.push(instance)
    this.instances.delete(key)
  }

  private processMountBudget(budgetMilliseconds: number): void {
    const deadline = performance.now() + budgetMilliseconds
    let operations = 0
    const staging = [...this.stagingInstances.entries()]
    for (const [key, instance] of staging) {
      if (performance.now() >= deadline || operations >= MOUNT_OPERATIONS_PER_FRAME) break
      const operationStartedAt = performance.now()
      instance.mountNextCriticalSurface()
      this.performanceDiagnostics.mountStepMilliseconds = Math.max(
        this.performanceDiagnostics.mountStepMilliseconds,
        performance.now() - operationStartedAt,
      )
      operations += 1
      if (instance.isReadyForPublication) this.publishChunk(key, instance)
    }
    if (performance.now() >= deadline || operations >= MOUNT_OPERATIONS_PER_FRAME) return
    const instances = [...this.instances.values()].filter((instance) => instance.hasPendingMeshes)
    if (instances.length === 0) return
    while (performance.now() < deadline && operations < MOUNT_OPERATIONS_PER_FRAME) {
      const instance = instances[this.mountCursor % instances.length]
      this.mountCursor += 1
      const operationStartedAt = performance.now()
      instance.mountNextMesh()
      this.performanceDiagnostics.mountStepMilliseconds = Math.max(
        this.performanceDiagnostics.mountStepMilliseconds,
        performance.now() - operationStartedAt,
      )
      operations += 1
    }
  }

  private async mountSpawnNeighbourhood(x: number, z: number, generation: number): Promise<void> {
    const instances = [...this.stagingInstances.entries()]
      .sort((left, right) => left[1].distanceToWorldPoint(x, z) - right[1].distanceToWorldPoint(x, z))
      .slice(0, SPAWN_NEIGHBOURHOOD_CHUNKS)
    let cursor = 0
    this.criticalMountInProgress = true
    try {
      for (const [key, instance] of instances) {
        if (instance.isReadyForPublication) this.publishChunk(key, instance)
      }
      while (instances.some(([, instance]) => instance.hasPendingCriticalSurfaces)) {
        const [key, instance] = instances[cursor % instances.length]
        cursor += 1
        if (!instance.hasPendingCriticalSurfaces) continue
        const operationStartedAt = performance.now()
        instance.mountNextCriticalSurface()
        const operationDuration = performance.now() - operationStartedAt
        this.performanceDiagnostics.mountStepMilliseconds = Math.max(
          this.performanceDiagnostics.mountStepMilliseconds,
          operationDuration,
        )
        this.performanceDiagnostics.criticalMountStepMilliseconds = Math.max(
          this.performanceDiagnostics.criticalMountStepMilliseconds,
          operationDuration,
        )
        this.addPerformanceEvent('critical-mount-step', operationStartedAt, operationDuration)
        if (instance.isReadyForPublication) this.publishChunk(key, instance)
        // One geometry per presentation opportunity also bounds the deferred
        // WebGL buffer upload, which CPU-only time budgets cannot observe.
        await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
        if (generation !== this.generation) return
      }
    } finally {
      this.criticalMountInProgress = false
    }
  }

  private processRetirementBudget(budgetMilliseconds: number): void {
    const startedAt = performance.now()
    const retired = this.retiredInstances[0]
    if (retired) {
      const complete = retired.disposeStep(192)
      if (complete) this.retiredInstances.shift()
    }
    const chunks = this.retiredMacroChunks[0]
    if (chunks) {
      for (let count = 0; count < 2 && chunks.length > 0; count += 1) chunks.pop()
      if (chunks.length === 0) this.retiredMacroChunks.shift()
    }
    const duration = performance.now() - startedAt
    this.performanceDiagnostics.retirementStepMilliseconds = Math.max(
      this.performanceDiagnostics.retirementStepMilliseconds,
      duration,
    )
    if (duration >= Math.min(1, budgetMilliseconds)) this.recordPerformanceEvent('retire-step', startedAt)
  }

  private evictDistantChunks(x: number, z: number): void {
    for (const [key, instance] of this.instances) {
      if (instance.distanceToWorldPoint(x, z) > EVICTION_RADIUS_METERS) this.removeChunk(key)
    }
    for (const [key, instance] of this.stagingInstances) {
      if (instance.distanceToWorldPoint(x, z) > EVICTION_RADIUS_METERS) this.removeChunk(key)
    }
  }

  private evictDistantMacros(x: number, z: number): void {
    for (const [key, runtime] of this.macros) {
      if (key === this.currentMacroKey) continue
      const centre = projectGeoPointToFrame(runtime.frame.anchor, this.sessionFrame)
      if (Math.hypot(centre.x - x, centre.z - z) <= MACRO_EVICTION_RADIUS_METERS) continue
      for (const chunk of runtime.chunks) this.removeChunk(tileKey(chunk.id))
      this.retiredMacroChunks.push(runtime.chunks)
      this.macros.delete(key)
    }
  }

  private prefetchAround(
    x: number,
    z: number,
    directionX: number,
    directionZ: number,
    intendedDestination?: PrefetchPoint,
  ): void {
    this.lastPrefetchProbe = { x, z }
    const targets = rankedPrefetchPoints(
      { x, z },
      { x: directionX, z: directionZ },
      PREFETCH_LOOKAHEAD_METERS,
      intendedDestination,
    )
    const candidate = selectSinglePrefetchCandidate(
      targets,
      (target) => {
        const macrotile = macrotileAtGeoPoint(this.localToGeo(target.x, target.z))
        return { key: tileKey(macrotile), value: macrotile }
      },
      new Set(this.macros.keys()),
      new Set(this.loadingMacros.keys()),
    )
    this.lastPrefetchCandidateKey = candidate?.key
    const previousPending = this.pendingPrefetch
    const rankedPending = candidate === undefined
      ? undefined
      : { macrotile: candidate.value, key: candidate.key, generation: this.generation }
    this.pendingPrefetch = retainPendingPrefetch(this.pendingPrefetch, rankedPending)
    if (
      candidate !== undefined
      && (previousPending?.key !== candidate.key || previousPending.generation !== this.generation)
    ) this.recordPrefetchTransition('queued', candidate.key, this.generation)
    this.drainPendingPrefetch()
  }

  private drainPendingPrefetch(): void {
    const pending = this.pendingPrefetch
    if (pending === undefined) return
    const action = pendingPrefetchAction(
      pending.generation,
      this.generation,
      pending.key,
      new Set(this.macros.keys()),
      this.loadingMacros.size,
    )
    if (action === 'wait') {
      this.recordPrefetchTransition('wait', pending.key, pending.generation)
      return
    }
    if (action === 'discard') {
      this.pendingPrefetch = undefined
      this.recordPrefetchTransition('discard', pending.key, pending.generation)
      return
    }
    this.pendingPrefetch = undefined
    this.prefetchLoads.add(pending.key)
    this.recordPrefetchTransition('start', pending.key, pending.generation)
    void this.ensureMacro(pending.macrotile, pending.generation, false).catch((cause) => {
      const message = cause instanceof Error ? cause.message : String(cause)
      if (!message.includes('superseded')) this.status = `STREAM RETRY NEEDED · ${message}`
    })
  }

  private findBestSpawn(runtime: RuntimeMacro): SpawnPoint {
    let best: { score: number; x: number; z: number; heading: number; chunkKey: string; sampleHeight: number } | undefined
    for (const chunk of runtime.chunks) {
      const roads = chunk.roads
      const samplesByRoad = new Map<number, number[]>()
      for (let sample = 0; sample < roads.roadIndex.length; sample += 1) {
        if ((roads.flags[sample] & ROAD_SAMPLE_FLAGS.path) !== 0) continue
        const roadIndex = roads.roadIndex[sample]
        const samples = samplesByRoad.get(roadIndex)
        if (samples) samples.push(sample)
        else samplesByRoad.set(roadIndex, [sample])
      }
      for (const [roadIndex, samples] of samplesByRoad) {
        if (samples.length < 3) continue
        const cumulative = new Float32Array(samples.length)
        for (let position = 1; position < samples.length; position += 1) {
          const previous = samples[position - 1] * 3
          const current = samples[position] * 3
          cumulative[position] = cumulative[position - 1] + Math.hypot(
            roads.positions[current] - roads.positions[previous],
            roads.positions[current + 2] - roads.positions[previous + 2],
          )
        }
        const totalLength = cumulative.at(-1) ?? 0
        const label = roads.roadIds[roadIndex] ?? 'UNNAMED ROAD'
        const servicePenalty = /^(SERVICE|DRIVEWAY|PARKING AISLE|TRACK|FOOTWAY)$/i.test(label) ? 260 : 0
        for (let position = 1; position < samples.length - 1; position += 1) {
          const sample = samples[position]
          const previous = samples[position - 1]
          const next = samples[position + 1]
          const previousGap = cumulative[position] - cumulative[position - 1]
          const nextGap = cumulative[position + 1] - cumulative[position]
          if (previousGap < 0.25 || previousGap > 14 || nextGap < 0.25 || nextGap > 14) continue
          const tangentX = roads.tangents[sample * 2]
          const tangentZ = roads.tangents[sample * 2 + 1]
          const previousAlignment = tangentX * roads.tangents[previous * 2] + tangentZ * roads.tangents[previous * 2 + 1]
          const nextAlignment = tangentX * roads.tangents[next * 2] + tangentZ * roads.tangents[next * 2 + 1]
          if (previousAlignment < 0.2 || nextAlignment < 0.2) continue
          const backwardDepth = cumulative[position]
          const forwardDepth = totalLength - backwardDepth
          const interiorDepth = Math.min(backwardDepth, forwardDepth)
          const robustThroughRoad = totalLength >= 48 && interiorDepth >= 16
          const x = roads.positions[sample * 3] + runtime.offset.x
          const z = roads.positions[sample * 3 + 2] + runtime.offset.z
          const width = roads.widths[sample]
          const separatedLayer = (roads.flags[sample] & (ROAD_SAMPLE_FLAGS.bridge | ROAD_SAMPLE_FLAGS.tunnel)) !== 0
          const junction = (roads.flags[sample] & ROAD_SAMPLE_FLAGS.junction) !== 0
          const score = Math.hypot(x, z)
            + (robustThroughRoad ? 0 : 420)
            + servicePenalty
            + (width < 4.8 ? 110 : 0)
            + (separatedLayer ? 35 : 0)
            + (junction ? 25 : 0)
            - Math.min(width, 16) * 7
            - Math.min(interiorDepth, 60) * 2.4
            - Math.min(totalLength, 180) * 0.25
          if (best && score >= best.score) continue
          const towardForward = forwardDepth >= backwardDepth
          const heading = Math.atan2(tangentX, tangentZ) + (towardForward ? 0 : Math.PI)
          best = { score, x, z, heading, chunkKey: tileKey(chunk.id), sampleHeight: roads.positions[sample * 3 + 1] }
        }
      }
    }
    return best
      ? { x: best.x, z: best.z, heading: best.heading, chunkKey: best.chunkKey, sampleHeight: best.sampleHeight }
      : { x: 0, z: 0, heading: 0 }
  }

  private clearWorld(): void {
    this.scene.remove(this.worldRoot)
    for (const instance of this.instances.values()) this.retiredInstances.push(instance)
    for (const instance of this.stagingInstances.values()) this.retiredInstances.push(instance)
    for (const runtime of this.macros.values()) this.retiredMacroChunks.push(runtime.chunks)
    this.instances.clear()
    this.stagingInstances.clear()
    this.worldRoot = new THREE.Group()
    this.worldRoot.name = 'STREAMED_WORLD'
    this.scene.add(this.worldRoot)
    this.macros.clear()
    this.loadingMacros.clear()
    this.pendingPrefetch = undefined
    this.motionPrefetchIntent = undefined
    this.lastUpdatePosition = undefined
    this.lastPrefetchProbe = undefined
    this.lastPrefetchCandidateKey = undefined
    this.prefetchLoads.clear()
    this.prefetchTransitions = []
    this.prefetchTransitionSequence = 0
    this.currentMacroKey = ''
    this.spawn = { x: 0, z: 0, heading: 0 }
    this.spawnDiagnostic = undefined
    this.lastSurfaceHeight = 0
  }

  private assertGeneration(generation: number): void {
    if (generation !== this.generation) throw new Error('World compile superseded by teleport.')
  }

  private recordPerformanceEvent(phase: string, startTime: number): void {
    this.addPerformanceEvent(phase, startTime, performance.now() - startTime)
  }

  private addPerformanceEvent(phase: string, startTime: number, duration: number): void {
    this.performanceEvents.push({ phase, startTime, duration })
    if (this.performanceEvents.length > 160) this.performanceEvents.splice(0, this.performanceEvents.length - 160)
  }

  private recordPrefetchTransition(
    event: StreamingPrefetchTransition['event'],
    key: string,
    generation: number,
  ): void {
    const previous = this.prefetchTransitions.at(-1)
    if (previous?.event === event && previous.key === key && previous.generation === generation) return
    this.prefetchTransitions.push({
      sequence: ++this.prefetchTransitionSequence,
      event,
      key,
      generation,
    })
    if (this.prefetchTransitions.length > 24) this.prefetchTransitions.shift()
  }
}
